import { defineStore } from 'pinia';
import { ref } from 'vue';
import type { Item } from '../api';

export interface UndoAction {
  id: string;
  type: 'delete' | 'batch-delete';
  items: Item[];
  timestamp: number;
  expiresAt: number;
}

export const useUndoStore = defineStore('undo', () => {
  const actions = ref<Map<string, UndoAction>>(new Map());
  let cleanupTimer: ReturnType<typeof setInterval> | null = null;

  // 启动清理过期操作的定时器
  function startCleanup() {
    if (cleanupTimer) return;

    cleanupTimer = setInterval(() => {
      const now = Date.now();
      for (const [id, action] of actions.value) {
        if (now > action.expiresAt) {
          actions.value.delete(id);
        }
      }
    }, 1000);
  }

  // 添加可撤销操作
  function addAction(action: Omit<UndoAction, 'id' | 'timestamp' | 'expiresAt'>): string {
    startCleanup();

    const id = `undo-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const now = Date.now();

    const undoAction: UndoAction = {
      ...action,
      id,
      timestamp: now,
      expiresAt: now + 5000, // 5秒后过期
    };

    actions.value.set(id, undoAction);
    return id;
  }

  // 获取操作
  function getAction(id: string): UndoAction | undefined {
    return actions.value.get(id);
  }

  // 执行撤销（移除操作并返回被删除的项目）
  function executeUndo(id: string): Item[] | null {
    const action = actions.value.get(id);
    if (!action) return null;

    actions.value.delete(id);
    return action.items;
  }

  // 移除操作（撤销超时后）
  function removeAction(id: string) {
    actions.value.delete(id);
  }

  // 清空所有操作
  function clearAll() {
    actions.value.clear();
  }

  return {
    actions,
    addAction,
    getAction,
    executeUndo,
    removeAction,
    clearAll,
  };
});
